import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { labelFuncion, labelNivel } from "@/lib/sepCatalogos";

const nombreCompleto = (p) => [p.nombre, p.apellido_paterno, p.apellido_materno].filter(Boolean).join(" ");

export default function MiPersonalPrint() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/mi-personal");
        setItems(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Cargando…</div>;

  const scopeLabel =
    user?.role === "supervisor_zona" ? "Supervisión de Zona"
    : user?.role === "jefe_sector" ? "Jefatura de Sector"
    : "Personal a cargo";
  const responsable = [user?.nombre, user?.apellido_paterno, user?.apellido_materno].filter(Boolean).join(" ") || user?.name || user?.email || "";
  const today = new Date().toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" });

  const totalH = items.filter((i) => i.sexo === "H").length;
  const totalM = items.filter((i) => i.sexo === "M").length;

  return (
    <div className="min-h-screen bg-[#EAECEE] print:bg-white p-6 print:p-0">
      <div className="mb-4 flex justify-end print:hidden max-w-[280mm] mx-auto">
        <button onClick={() => window.print()} className="px-3 py-1.5 rounded bg-[#235B4E] text-white text-sm" data-testid="btn-print-mi-personal-page">
          Imprimir / PDF
        </button>
      </div>
      <div className="hoja bg-white mx-auto shadow print:shadow-none relative" style={{ fontFamily: "'IBM Plex Sans', Georgia, serif", color: "#1F2937" }}>
        {/* Barra tricolor superior */}
        <div className="flex h-2">
          <div className="flex-1 bg-[#235B4E]" />
          <div className="flex-1 bg-white" />
          <div className="flex-1 bg-[#9F2241]" />
        </div>
        <div style={{ padding: "10mm 12mm" }}>
          <header className="border-b-2 border-[#7A1E37] pb-3 mb-4 flex items-start justify-between">
            <div>
              <div className="text-[9pt] uppercase tracking-[0.3em] text-[#8F6B1F]">Reporte de personal a cargo</div>
              <div className="text-[18pt] font-bold uppercase text-[#5C1428]" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.03em" }}>
                {scopeLabel}
              </div>
              <div className="text-[10pt] text-[#4B5563] mt-1">
                Responsable: <strong className="uppercase">{responsable}</strong>
              </div>
            </div>
            <div className="text-right text-[9pt] text-[#4B5563]">
              <div>Fecha de emisión</div>
              <div className="font-semibold text-[#1F2937]">{today}</div>
              <div className="mt-1">Total registros: <strong>{items.length}</strong></div>
              <div>Hombres: <strong>{totalH}</strong> · Mujeres: <strong>{totalM}</strong></div>
            </div>
          </header>

          {items.length === 0 ? (
            <div className="p-8 text-center text-[#6B7280]">Sin personal registrado.</div>
          ) : (
            <table className="w-full border-collapse text-[8.5pt]">
              <thead>
                <tr className="bg-[#5C1428] text-white uppercase text-[7.5pt] tracking-wider">
                  <th className="border border-[#5C1428] px-1.5 py-1.5 text-center w-8">#</th>
                  <th className="border border-[#5C1428] px-1.5 py-1.5 text-left">Nombre completo</th>
                  <th className="border border-[#5C1428] px-1.5 py-1.5 text-center w-10">Sexo</th>
                  <th className="border border-[#5C1428] px-1.5 py-1.5 text-left">Función</th>
                  <th className="border border-[#5C1428] px-1.5 py-1.5 text-left">Nivel educativo</th>
                  <th className="border border-[#5C1428] px-1.5 py-1.5 text-left">CURP</th>
                  <th className="border border-[#5C1428] px-1.5 py-1.5 text-left">RFC</th>
                  <th className="border border-[#5C1428] px-1.5 py-1.5 text-left">Plaza</th>
                  <th className="border border-[#5C1428] px-1.5 py-1.5 text-center w-16">Celular</th>
                  <th className="border border-[#5C1428] px-1.5 py-1.5 text-left">Correo personal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it.id} className={idx % 2 === 0 ? "bg-white" : "bg-[#FBF7F2]"}>
                    <td className="border border-[#D1D5DB] px-1.5 py-1 text-center">{idx + 1}</td>
                    <td className="border border-[#D1D5DB] px-1.5 py-1 font-medium">{nombreCompleto(it).toUpperCase()}</td>
                    <td className="border border-[#D1D5DB] px-1.5 py-1 text-center">{it.sexo}</td>
                    <td className="border border-[#D1D5DB] px-1.5 py-1">{labelFuncion(it.funcion)}</td>
                    <td className="border border-[#D1D5DB] px-1.5 py-1">
                      {labelNivel(it.nivel_educativo)}
                      {it.nivel_educativo === "OTROS" && it.nivel_educativo_otros && (
                        <span className="text-[7pt] text-[#6B7280]"> ({it.nivel_educativo_otros})</span>
                      )}
                    </td>
                    <td className="border border-[#D1D5DB] px-1.5 py-1 font-mono text-[7.5pt]">{it.curp || "—"}</td>
                    <td className="border border-[#D1D5DB] px-1.5 py-1 font-mono text-[7.5pt]">{it.rfc || "—"}</td>
                    <td className="border border-[#D1D5DB] px-1.5 py-1 font-mono text-[7.5pt]">{it.plaza || "—"}</td>
                    <td className="border border-[#D1D5DB] px-1.5 py-1 text-center font-mono text-[7.5pt]">{it.celular || "—"}</td>
                    <td className="border border-[#D1D5DB] px-1.5 py-1 text-[7.5pt]">{it.correo_personal || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="mt-8 grid grid-cols-2 gap-16 pt-6">
            <div />
            <div className="text-center">
              <div style={{ borderTop: "1px solid #5C1428", paddingTop: "2mm" }}>
                <div className="text-[9pt] font-bold uppercase text-[#5C1428]" style={{ fontFamily: "'Montserrat', sans-serif" }}>{responsable}</div>
                <div className="text-[7.5pt] uppercase tracking-[0.25em] text-[#8F6B1F]">{scopeLabel}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @page { size: Letter landscape; margin: 0; }
        .hoja { width: 279mm; min-height: 216mm; box-sizing: border-box; }
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; background: white; }
          .hoja { box-shadow: none !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
}
